import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";

import {
  useFormatCurrency,
  useGetCurrentLoanApplicationID,
} from "@krea/customer-features/hooks";
import ArrowSwitchSVG from "@krea/customer-features/images/svg/switch-arrows.svg";
import { settings } from "@krea/customer-features/settings";
import ExpandAnimation from "@krea/customer-features/shared-components/animations/Expand";
import { Text } from "@krea/customer-features/shared-components/text";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useChangeCustomerPreferredRate } from "@krea/customer-features/store/current-loan-application/hooks/useChangeCustomerPreferredRate";
import {
  ICompanyLoanBid,
  IInvoicePurchaseBid,
  ILeasingBid,
  IRealEstateBid,
  IResolvedBid,
  IRevolvingCreditBid,
} from "@krea/customer-features/types/lenderResponse";
import { RATE_TO_BID_KEY_MAP } from "@krea/customer-features/types/loanApplication";
import {
  BID_TYPE,
  FormatNumber,
  NormalizeRequestedPaymentTimeMonths,
} from "@krea/customer-features/utils";

import BidDetailsItem from "./BidDetailsItem";
import BidDetailsList from "./BidDetailsList";

const FactoringBidDetails = loadable(
  () => import("../bid-dashboards/factoring-bids/FactoringBidDetails"),
);
const LeasingBidDetails = loadable(
  () => import("../bid-dashboards/leasing-bids/LeasingBidDetails"),
);
const RevolvingCreditBidDetails = loadable(
  () =>
    import("../bid-dashboards/revolving-credit-bids/RevolvingCreditBidDetails"),
);

interface IBidDetails {
  className?: string;
  bidType: BID_TYPE;
  bid:
    | IRealEstateBid
    | IInvoicePurchaseBid
    | ICompanyLoanBid
    | ILeasingBid
    | IRevolvingCreditBid;
  withAnimation?: boolean;
  isPostSelect?: boolean;
  lenderCompanyName?: string;
}

const BidDetails = ({
  className,
  bidType,
  bid,
  withAnimation,
  isPostSelect = false,
  lenderCompanyName,
}: IBidDetails) => {
  const { t } = useTranslation();
  const currency = settings.currency;
  const [formattedCurrencyValue] = useFormatCurrency({ currency });

  const { data } = useGetCachedCurrentLoanApplication();
  const { customerPreferredRate, alternativeRate } = data ?? {};

  const loanApplicationID = useGetCurrentLoanApplicationID();
  const { mutate: changeCustomerPreferredRate } =
    useChangeCustomerPreferredRate(loanApplicationID!);

  const rateKey = RATE_TO_BID_KEY_MAP[customerPreferredRate!];

  const content = (bid: IResolvedBid) => {
    switch (bidType) {
      case BID_TYPE.FACTORING:
        if ("paidOutPerInvoice" in bid) {
          return <FactoringBidDetails bid={bid} />;
        }
        break;

      case BID_TYPE.LEASING:
        if ("downPayment" in bid) {
          return <LeasingBidDetails bid={bid} />;
        }
        break;

      case BID_TYPE.REVOLVING_CREDIT:
        if ("withdrawalFee" in bid) {
          return <RevolvingCreditBidDetails bid={bid} />;
        }
        break;

      default:
        if ("amortizationMethod" in bid) {
          return (
            <BidDetailsList className={className}>
              {lenderCompanyName && (
                <BidDetailsItem
                  title={t("bidDetails.lender")}
                  value={lenderCompanyName}
                />
              )}

              {isPostSelect && (
                <>
                  <BidDetailsItem
                    title={t("dash.loanBids.columns.approvedAmount")}
                    value={formattedCurrencyValue(bid.amount)}
                  />

                  <BidDetailsItem
                    title={t("bidDetails.repaymentTime")}
                    value={NormalizeRequestedPaymentTimeMonths(
                      bid.paybackTimeMonths,
                    )}
                  />

                  <BidDetailsItem
                    title={t("bidDetails.averageMonthlyPayment")}
                    value={formattedCurrencyValue(bid.monthlyCost)}
                  />
                </>
              )}

              {bid.amortizationMethod !== "FIXED" ? (
                <BidDetailsItem
                  title={t("bidDetails.contractInterest")}
                  value={`${FormatNumber(bid.lenderMonthlyInterest)}%`}
                />
              ) : null}

              {/* <BidDetailsItem
                title={t('bidDetails.averageMonthlyCost')}
                value={formattedCurrencyValue(bid.monthlyCostWithoutSetupFee)}
              /> */}

              {/* {bid.loanToValueRatio && bid.loanToValueRatio > 0 && (
              <BidDetailsItem
                title={t('bidDetails.loanToValue')}
                value={`${FormatNumber(bid.loanToValueRatio)}%`}
              />
            )} */}

              <BidDetailsItem
                title={t("bidDetails.amortisation")}
                value={bid.amortizationMethodDisplayName}
                tooltip={bid.amortizationMethodDescription}
              />

              {bidType !== BID_TYPE.REAL_ESTATE &&
                "isRepayableWithoutFee" in bid && (
                  <BidDetailsItem
                    title={t("bidDetails.earlyRepaymentFee")}
                    value={
                      !bid.isRepayableWithoutFee
                        ? bid.earlyRepaymentFee
                        : t("bidDetails.repayableWithoutFee")
                    }
                  />
                )}
              {/* {!!bid.setupFee && (
              <BidDetailsItem
                title={t('bidDetails.setupFee')}
                value={formattedCurrencyValue(bid.setupFee)}
              />
            )} */}

              <BidDetailsItem
                title={t("bidDetails.setupFee")}
                value={
                  bid.setupFee && bid.setupFee > 0
                    ? formattedCurrencyValue(bid.setupFee)
                    : t("bidDetails.repayableWithoutFee")
                }
              />

              {!!bid.postalFee && (
                <BidDetailsItem
                  title={t("bidDetails.postalFee")}
                  value={formattedCurrencyValue(bid.postalFee)}
                />
              )}
              {!!bid.directDebitFee && (
                <BidDetailsItem
                  title={t("bidDetails.directDebitFee")}
                  value={formattedCurrencyValue(bid.directDebitFee)}
                />
              )}
              {/* <BidDetailsItem
              total
              title={t('bidDetails.totalCost')}
              value={formattedCurrencyValue(bid.totalLoanCost)}
            />
            <Text size="sm" className="font-weight-bold text-gray-dark">
              {t('bidDetails.totalCostExplainer')}
            </Text> */}

              {bid[rateKey] ? (
                <>
                  <hr className="tw-m-0 tw-mb-[16px] tw-border-t-2 tw-border-dotted" />
                  <div className="md:tw-flex md:tw-justify-between">
                    <Text size="md" className="tw-font-bold tw-text-gray-600">
                      {t("bidDetails.keyCalculations")}
                    </Text>

                    {alternativeRate && (
                      <div
                        className="tw-mt-1 tw-inline-block tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-primary tw-bg-primary-150 tw-px-2 tw-py-[3px] tw-align-middle md:tw-mt-0 md:tw-flex"
                        onClick={() => changeCustomerPreferredRate()}
                        onKeyUp={() => changeCustomerPreferredRate()}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="tw-mr-3 tw-flex tw-align-middle">
                          <div className="tw-relative tw-mr-2 tw-block tw-w-[16px] before:tw-p-0 before:tw-pb-[100%]">
                            <SVG
                              className="tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full"
                              src={ArrowSwitchSVG}
                            />
                          </div>
                          <div className="tw-text-nowrap tw-text-sm tw-font-semibold tw-leading-normal tw-text-primary">
                            {t(
                              `dash.bidsRateToaster.${RATE_TO_BID_KEY_MAP[alternativeRate]}.showText`,
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <BidDetailsItem
                    title={t(`dash.commonBidsColumns.${rateKey}.title`)}
                    value={`${bid[rateKey]}%`}
                  />

                  <BidDetailsItem
                    title={
                      rateKey === "firstMonthInterestRate"
                        ? t("bidDetails.firstMonthCost")
                        : t("bidDetails.averageMonthlyCost")
                    }
                    value={
                      rateKey === "firstMonthInterestRate"
                        ? formattedCurrencyValue(bid.firstMonthCost)
                        : formattedCurrencyValue(bid.monthlyCostWithoutSetupFee)
                    }
                  />
                </>
              ) : (
                <></>
              )}
            </BidDetailsList>
          );
        }
        break;
    }
  };

  return withAnimation ? (
    <ExpandAnimation className="">{content(bid)}</ExpandAnimation>
  ) : (
    <>{content(bid)}</>
  );
};

export default BidDetails;
